import * as Sentry from '@sentry/angular';
import type { Provider } from '@angular/core';
import { APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import type { BrowserOptions } from '@sentry/browser';
import { DateTime } from 'luxon';
import { HttpErrorResponse } from '@angular/common/http';

const sentryOptions = window.CareLineLive?.config?.sentry;

export const isSentryEnabled = !!sentryOptions?.dsn;

/**
 * Initialise the Sentry SDK
 */
export function initSentry(project: 'auth' | 'shell' | 'care-circle' | 'embedded' | 'document-signature'): void {
    if (!isSentryEnabled) {
        console.warn('Sentry is not enabled');
        return;
    }

    const options: BrowserOptions = {
        dsn: sentryOptions.dsn,
        release: sentryOptions.release,
        environment: sentryOptions.environment,
        allowUrls: sentryOptions.allowUrls,
        tracesSampleRate: 0,
        initialScope: {
            tags: {
                project: project,
                tenant: window.CareLineLive.tenant,
                cluster: window.CareLineLive.cluster,
                server_timezone: window.CareLineLive.display_timezone,
            },
            user: {
                id: window.CareLineLive?.user?.id,
                email: window.CareLineLive?.user?.email,
                geo: {
                    country_code: window.CareLineLive.country,
                },
            },
            contexts: {
                culture: {
                    locale: window.CareLineLive.js_locale,
                    currency: window.CareLineLive.currency_code,
                    timezone: DateTime.local().zoneName,
                },
            },
        },
        integrations: [
            Sentry.extraErrorDataIntegration(),
            Sentry.sessionTimingIntegration(),
        ],

        beforeSend(event, hint) {
            const originalException = hint.originalException;

            if (typeof originalException === 'string') {
                // Parse the exception message: "Http failure response for https://carelinelive.test/api/v1/alerts/count?acknowledged=false: 405 OK"
                if (originalException.startsWith('Http failure response')) {
                    // Extract the URL and status code from the message
                    const match = originalException.match(/for (.+): (\d+) OK/);

                    if (match) {
                        const url = match[1];
                        const statusCode = parseInt(match[2], 10);

                        const ignoredStatusCodes = [
                            400, // Bad Request
                            401, // Unauthorized
                            403, // Forbidden
                            404, // Not Found
                            408, // Request Timeout
                            422, // Unprocessable Entity
                            429, // Too Many Requests
                            0,   // Unknown Error
                        ];

                        // Check if the URL is from the same host
                        if (url.includes(location.host)) {
                            // Check if the status code is in the ignored list
                            if (ignoredStatusCodes.includes(statusCode)) {
                                console.log('sentry: Ignoring error', originalException);
                                // Ignore the error
                                return null;
                            }
                        }

                        // Add the URL and status code to the event context
                        event.contexts.response = {
                            url,
                            status_code: statusCode,
                        };
                    }

                }
            }

            if (originalException instanceof HttpErrorResponse) {
                // Don't send errors for server-side API exceptions
                if (originalException.url?.includes(location.host)) {
                    return null;
                }
            }

            return event;
        }
    };

    try {
        Sentry.init(options);
    } catch (e) {
        // Ironically, we can't use Sentry here because it's not initialized yet
        console.error('Error initializing Sentry', e);
    }
}

export function sentryProviders(): Provider[] {
    if (!isSentryEnabled) {
        return [];
    }

    return [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        }, {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {
            },
            deps: [Sentry.TraceService],
            multi: true,
        },
    ];
}
